import {
  Placement as PopperJSPlacement,
  hide as hideModifier,
  preventOverflow,
  flip as flipModifier,
  detectOverflow,
  Modifier,
} from '@popperjs/core';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { usePopper } from 'react-popper';

// popper.js's `flip` only switches sides when the *opposite* side fits
// outright; when the panel is too tall for either side it silently keeps the
// requested placement (`flip` falls back to `placements[0]`). For a tall edit
// panel anchored to a row near the top of a short viewport that means "top"
// wins even when the space below the row is twice as large, so the panel gets
// squeezed into the sliver above and has to scroll. This modifier adds the
// missing best-fit fallback: when the panel is up against the limit of the
// space at its resolved placement and the opposite side has meaningfully more
// room, flip to it.
//
// Space is derived the same way `maxSize` derives it -- `popperHeight -
// overflow[side]` at a candidate placement -- which algebraically reduces to
// the gap between the reference element and the clipping boundary. The
// popper's own height cancels out, so the *comparison* between the two sides
// is never skewed by the panel having already been capped by `maxSize`.
//
// The fit test can't be "does the panel overflow", though: `maxSize` caps the
// panel to the space at the resolved placement, so once it has run the panel
// measures exactly the sliver it was squeezed into and reads as fitting there
// forever. The signal is instead whether the panel has room *to spare* -- a
// panel that ends flush against the boundary is either overflowing or has
// already been capped, and both cases want the roomier side.
//
// The tolerance keeps sub-pixel rect math from flipping a panel that is
// effectively equally cramped on both sides.
const PLACEMENT_ROOM_TOLERANCE_PX = 8;

export const preferMoreRoomModifier: Modifier<'preferMoreRoom', Record<string, never>> = {
  name: 'preferMoreRoom',
  enabled: true,
  phase: 'main',
  requires: ['flip'],
  fn({ state, name }) {
    // Same one-shot guard `flip` uses on itself: `modifiersData` is
    // re-initialized from each modifier's `data` at the top of every update(),
    // so this only suppresses re-entry within a single update cycle.
    if ((state.modifiersData[name] as { _skip?: boolean } | undefined)?._skip) return;

    const [basePlacement, variation] = state.placement.split('-');
    if (basePlacement !== 'top' && basePlacement !== 'bottom') return;

    const oppositeBase = basePlacement === 'top' ? 'bottom' : 'top';
    const oppositePlacement = (variation ? `${oppositeBase}-${variation}` : oppositeBase) as PopperJSPlacement;

    const { height } = state.rects.popper;
    const room = height - detectOverflow(state, { placement: state.placement })[basePlacement];
    const oppositeRoom = height - detectOverflow(state, { placement: oppositePlacement })[oppositeBase];

    const hasRoomToSpareHere = height < room - PLACEMENT_ROOM_TOLERANCE_PX;
    if (hasRoomToSpareHere || oppositeRoom <= room + PLACEMENT_ROOM_TOLERANCE_PX) return;

    // `flip` runs again after the reset below and, seeing that neither side
    // fits, would put us straight back on the requested placement. Claim its
    // own skip flag so this decision is the final one for this cycle.
    const flipData = state.modifiersData.flip as { _skip?: boolean } | undefined;
    if (flipData) flipData._skip = true;

    state.modifiersData[name] = { _skip: true };
    state.placement = oppositePlacement;
    state.reset = true;
  },
};

// Measures however much vertical space is actually available at the
// popper's resolved placement (rather than guessing a flat viewport
// percentage). Deliberately exposed as a CSS custom property (below) rather
// than applied directly to the tracked popper element's own maxHeight:
// mutating that element's box would feed back into the *next* update's
// state.rects.popper, making `flip` see an artificially-already-fitting box
// and never switch to a placement with genuinely more room (e.g. a preferred
// top placement with lots of space below the trigger -- flip would stop
// treating the top side as overflowing once our own cap hides it, and never
// try the bottom side). Consumers that need to clip/scroll read
// --popper-available-height on their own inner element instead, so popper.js
// keeps positioning against the panel's true size.
export const maxSizeModifier: Modifier<'maxSize', Record<string, never>> = {
  name: 'maxSize',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['offset', 'preventOverflow', 'flip', 'preferMoreRoom'],
  fn({ state, name }) {
    const overflow = detectOverflow(state);
    const { height } = state.rects.popper;
    const [basePlacement] = state.placement.split('-');
    const heightProp = basePlacement === 'top' ? 'top' : 'bottom';

    state.modifiersData[name] = {
      height: height - overflow[heightProp],
    };
  },
};

export const applyMaxSizeModifier: Modifier<'applyMaxSize', Record<string, never>> = {
  name: 'applyMaxSize',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['maxSize'],
  fn({ state }) {
    const { height } = state.modifiersData.maxSize as { height: number };
    const popperStyle = state.styles.popper as Record<string, string>;

    popperStyle['--popper-available-height'] = `${Math.max(height, 0)}px`;
  },
};

type UsePopperReturnType = ReturnType<typeof usePopper>;
type UsePopperParameters = Parameters<typeof usePopper>;

export interface PopperContainerChildrenArgs {
  popperStyles: UsePopperReturnType['styles'];
  forceFloatingElementUpdate: (() => void) | null;
  popperAttributes: UsePopperReturnType['attributes'];
  setPopperEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
}

export interface PopperContainerProps {
  /**
   * [displacement along the reference element, displacement away from the reference element].
   *
   * Check https://popper.js.org/docs/v2/modifiers/offset/ for details.
   */
  offset?: [number, number];
  placement?: PopperJSPlacement;
  childrenEl: UsePopperParameters[0];
  children: (c: PopperContainerChildrenArgs) => JSX.Element;
  popperJSFlipModifierOptions?: typeof flipModifier['options'];
  popperJSPreventOverflowOptions?: typeof preventOverflow['options'];
  popperJSHideModifierOptions?: Partial<Omit<typeof hideModifier, 'name'>>;
}

export const PopperContainer: FC<PopperContainerProps> = ({
  offset,
  children,
  placement,
  childrenEl,
  popperJSFlipModifierOptions,
  popperJSPreventOverflowOptions,
  popperJSHideModifierOptions,
}) => {
  const [popperEl, setPopperEl] = useState<HTMLDivElement | null>(null);

  const {
    forceUpdate,
    styles: popperStyles,
    attributes: popperAttributes,
  } = usePopper(childrenEl, popperEl, {
    strategy: 'fixed',
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offset ?? [0, 0],
        },
      },
      { name: 'flip', options: popperJSFlipModifierOptions },
      {
        name: 'preventOverflow',
        options: popperJSPreventOverflowOptions,
      },
      { name: 'hide', ...popperJSHideModifierOptions },
      preferMoreRoomModifier,
      maxSizeModifier,
      applyMaxSizeModifier,
    ],
  });

  return (
    <>
      {children({
        setPopperEl,
        popperStyles,
        popperAttributes,
        forceFloatingElementUpdate: forceUpdate,
      })}
    </>
  );
};
